import {QuotesClient} from '../../../infrastracture/api/QuotesClient'
import {fetchMergedChargesForWorkOrders} from './quoteAmountsWithCharges'

export type ParentWorkOrderQuotesBundle = {
  successful: boolean
  /** Status groups from `GetQuotesForParentWorkOrder` (same shape as API). */
  groups: any[]
  fullQuotes: any[]
  chargesList: any[]
  /** Raw `response.data` from the list call (e.g. may carry `total` for pagination). */
  listPayload: any
}

/**
 * Collects quote ids from parent-work-order groups and returns deduplicated string ids.
 */
export function collectQuoteIdsFromParentGroups(groups: any[]): string[] {
  const allQuoteIds = groups.reduce((acc: string[], group: any) => {
    const sIds = (group.submittedQuotes || []).map((q: any) => q.id)
    const aIds = (group.approvedQuotes || []).map((q: any) => q.id)
    const cIds = (group.cancelledQuotes || []).map((q: any) => q.id)
    return [...acc, ...sIds, ...aIds, ...cIds]
  }, [])
  return Array.from(
    new Set(
      allQuoteIds
        .filter((id: string | undefined): id is string => id != null && String(id).trim() !== '')
        .map(String)
    )
  )
}

/**
 * Shared pipeline used by parent WO quotes list and project cost modal:
 * list quotes → full `Get` per id (deduped) → merged active charges for parent + child WOs.
 */
export async function fetchParentWorkOrderQuotesBundle(
  parentWorkOrderId: string
): Promise<ParentWorkOrderQuotesBundle> {
  const pid = String(parentWorkOrderId)
  const response = await QuotesClient.GetQuotesForParentWorkOrder(pid)

  if (!response.successful) {
    return {successful: false, groups: [], fullQuotes: [], chargesList: [], listPayload: null}
  }

  const listPayload = response.data
  const groups = Array.isArray(listPayload) ? listPayload : []

  const uniqueIds = collectQuoteIdsFromParentGroups(groups)
  const fullResponses = await Promise.all(uniqueIds.map((id) => QuotesClient.Get(id)))
  const fullQuotes = fullResponses
    .filter((res: any) => res.successful)
    .map((res: any) => res.data)

  const woIds = new Set<string>([pid])
  for (const q of fullQuotes) {
    if (q?.workOrderId) woIds.add(String(q.workOrderId))
  }
  const chargesList = await fetchMergedChargesForWorkOrders(Array.from(woIds))

  return {
    successful: true,
    groups,
    fullQuotes,
    chargesList,
    listPayload,
  }
}
